/**
 * Open-shift writes (BON-240): publish + withdraw against /api/v1/open-shifts.
 * The board READ comes through the dashboards envelope
 * (useRegionalShiftBoard in regional/regionalApi.ts); this module is the
 * write side only. Row shape mirrors OpenShiftEntry in
 * backend/src/services/open-shifts.service.ts — keep in sync.
 */
import { apiFetch } from '../lib/apiFetch';

import type { CrewPosition } from './workshops';

/** The single frontend declaration of the open-shift row — the board type
 *  (OpenShiftBoardEntry in regional/regionalApi.ts) aliases this. */
export interface OpenShiftWriteRow {
  id: string;
  workshopId: string;
  startsAtIso: string;
  venueShort: string;
  workshopTypeName: string;
  position: string;
  /** `cancelled` = the workshop died and took the offer with it (BON-396).
   *  `filled` = an RD confirmed someone into it (BON-243). The live board
   *  returns neither; shift history will. */
  status: 'open' | 'withdrawn' | 'cancelled' | 'filled';
  publishedAtIso: string;
}

/** Someone waiting on a shift — mirrors ShiftRequester in
 *  backend/src/services/shift-confirm.service.ts. */
export interface ShiftRequester {
  userId: string;
  fullName: string;
  email: string | null;
  requestedAtIso: string;
}

/** What one confirm/decline did — mirrors DecisionOutcome server-side. */
export interface ShiftDecisionOutcome {
  openShiftId: string;
  workshopId: string;
  confirmed: ShiftRequester | null;
  declined: ShiftRequester[];
}

/** Emails are sent post-commit, so a send failure does NOT undo the decision.
 *  The caller surfaces `failed > 0` as a warning, never as a failed confirm. */
export interface ShiftDecisionNotified {
  sent: number;
  failed: number;
}

export interface ShiftDecisionResult {
  outcome: ShiftDecisionOutcome;
  notified: ShiftDecisionNotified;
}

async function parseDecision(res: Response, fallback: string): Promise<ShiftDecisionResult> {
  let body: (Partial<ShiftDecisionResult> & { message?: string }) | null = null;
  try {
    body = (await res.json()) as Partial<ShiftDecisionResult> & { message?: string };
  } catch {
    body = null;
  }
  if (!res.ok || !body?.outcome || !body.notified) {
    throw new Error(body?.message ?? `${fallback} (HTTP ${res.status})`);
  }
  return { outcome: body.outcome, notified: body.notified };
}

/** Confirm one requester into the crew. Fills the shift and auto-declines
 *  everyone else waiting on it. */
export async function confirmShiftPickup(input: {
  shiftId: string;
  userId: string;
}): Promise<ShiftDecisionResult> {
  const res = await apiFetch(`/api/v1/open-shifts/${input.shiftId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ userId: input.userId }),
  });
  return parseDecision(res, 'Failed to confirm the shift');
}

/** Turn one requester away. The shift stays open for everyone else. */
export async function declineShiftPickup(input: {
  shiftId: string;
  userId: string;
}): Promise<ShiftDecisionResult> {
  const res = await apiFetch(`/api/v1/open-shifts/${input.shiftId}/decline`, {
    method: 'POST',
    body: JSON.stringify({ userId: input.userId }),
  });
  return parseDecision(res, 'Failed to decline the request');
}

async function parseShift(res: Response, fallback: string): Promise<OpenShiftWriteRow> {
  let body: { shift?: OpenShiftWriteRow; message?: string } | null = null;
  try {
    body = (await res.json()) as { shift?: OpenShiftWriteRow; message?: string };
  } catch {
    body = null;
  }
  if (!res.ok || !body?.shift) {
    throw new Error(body?.message ?? `${fallback} (HTTP ${res.status})`);
  }
  return body.shift;
}

/** Publish an open shift for a position on a workshop. */
export async function publishOpenShift(input: {
  workshopId: string;
  position: CrewPosition;
}): Promise<OpenShiftWriteRow> {
  const res = await apiFetch('/api/v1/open-shifts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return parseShift(res, 'Failed to publish the shift');
}

/** Withdraw a published shift (soft — it disappears from the board). */
export async function withdrawOpenShift(shiftId: string): Promise<OpenShiftWriteRow> {
  const res = await apiFetch(`/api/v1/open-shifts/${shiftId}/withdraw`, { method: 'POST' });
  return parseShift(res, 'Failed to withdraw the shift');
}
